import React from 'react';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import useStyles from './styles';
import clsx from 'clsx';

// Helper to filter out invalid React nodes (e.g., empty objects from Storybook)
const isValidReactNode = (node: React.ReactNode): boolean => {
  if (node === null || node === undefined || typeof node === 'boolean') {
    return false;
  }
  if (typeof node === 'string') {
    return true; // Strings are always valid (even empty strings)
  }
  if (typeof node === 'object') {
    // Check if it's an empty object (invalid)
    if (Object.keys(node).length === 0) {
      return false;
    }
    // Check if it's a valid React element
    if (React.isValidElement(node)) {
      return true;
    }
    // If it's an object but not a React element, it's likely invalid
    return false;
  }
  return true;
};

const PageTitleDescription = (props: PageTitleDescriptionProps) => {
  const { classes } = useStyles();
  const {
    pageTitle,
    description,
    divider = true,
    disablePadding = false,
    disableTitleGutter,
    className,
    children,
  } = props;

  // Filter out invalid description values (e.g., empty objects from Storybook)
  const safeDescription = isValidReactNode(description) ? description : undefined;

  return (
    <div
      className={clsx(
        classes.root,
        {
          [classes.padding]: !disablePadding,
        },
        className,
      )}
    >
      <Typography
        className={classes.pageTitle}
        variant={'h1'}
        gutterBottom={!disableTitleGutter}
      >
        {pageTitle}
      </Typography>

      {divider && <Divider className={classes.divider} variant={'fullWidth'} />}

      {safeDescription && (
        <Typography component={'span'} variant="body1" gutterBottom>
          {safeDescription}
        </Typography>
      )}

      {isValidReactNode(children) && children}
    </div>
  );
};

export type PageTitleDescriptionProps = {
  pageTitle: string;
  divider?: boolean;
  disablePadding?: boolean;
  disableTitleGutter?: boolean;
  description?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
};

export default PageTitleDescription;
